import { z } from 'zod';
import type { DataClassification } from './vocabularies';
import {
  AllocationBasis,
  AllocationStatus,
  ClassificationClass,
  ConfidenceBand,
  Criticality,
  DecisionStatus,
  Disposition,
  EvidenceStatus,
  FunctionalPermission,
  IngestionRunStatus,
  IssueSeverity,
  Lifecycle,
  MappingRuleLifecycle,
  OrganizationalUnitType,
  ProcessLevel,
  ProductHealth,
  TechnicalHealth,
  TransformationType,
} from './vocabularies';
import { IsoDate, Timestamp, Uuid, withEnvelope } from './envelope';
import { MetricUnit, Money, SourceMode } from './metric';
import { ScenarioType } from './read-models';

/**
 * The 61 canonical entities, ENT-001…ENT-061.
 *
 * Source: Canonical Entity Model §3, grouped by the fourteen domains of its
 * Appendix A. Each schema declares only what is specific to the entity; the
 * common envelope (§2) supplies id, tenant, businessCode, name, description,
 * status, rowVersion, effective dates, audit stamps, source identity,
 * classification, quality and confidence.
 *
 * Two conventions are worth knowing before reading further:
 *
 *  - The catalog's per-entity code attribute (`productCode`, `unitCode`,
 *    `runCode`, …) is the envelope's `businessCode`. It is not repeated here,
 *    because a second code column is how two competing identifiers get created.
 *
 *  - The envelope types `status` as a plain string so that each entity can
 *    narrow it to its own governed vocabulary. Where such a vocabulary exists
 *    the schema below redeclares `status`; that is deliberate, and it is what
 *    stops an ingestion run being saved with a decision status.
 *
 * Attributes the catalog names but the envelope already carries (a DataAsset's
 * `sensitivity`, a MetricObservation's `sourceRunId`, a WorkItem's `sourceKey`)
 * are likewise not repeated.
 */

// ── Organization (ENT-001…004) ───────────────────────────────────────────────

export const Enterprise = withEnvelope('Enterprise', {
  legalName: z.string().min(1),
  operatingName: z.string().min(1).nullable(),
  /** IANA zone. Reporting periods are cut in the enterprise's own day. */
  timezone: z.string().min(1),
});
export type Enterprise = z.infer<typeof Enterprise>;

export const OrganizationalUnit = withEnvelope('OrganizationalUnit', {
  unitType: OrganizationalUnitType,
  /**
   * The parent as of `validFrom`. Traversal must still go through the governed
   * CONTAINS relationship for as-of queries — this column is a convenience for
   * the current view, never the historical truth.
   */
  parentUnitId: Uuid.nullable(),
  leaderId: Uuid.nullable(),
});
export type OrganizationalUnit = z.infer<typeof OrganizationalUnit>;

export const ValueStream = withEnvelope('ValueStream', {
  purpose: z.string().nullable(),
  ownerId: Uuid.nullable(),
  outcomeSummary: z.string().nullable(),
});
export type ValueStream = z.infer<typeof ValueStream>;

export const ProductGroup = withEnvelope('ProductGroup', {
  strategy: z.string().nullable(),
  ownerId: Uuid.nullable(),
  lifecycle: Lifecycle,
});
export type ProductGroup = z.infer<typeof ProductGroup>;

// ── Product (ENT-005…010) ────────────────────────────────────────────────────

export const Product = withEnvelope('Product', {
  /**
   * Nullable because a product may exist before its classification is approved.
   * The approved class lives on ProductClassification; this is the effective
   * denormalization, and it must be written only from an approved version.
   */
  classCode: ClassificationClass.nullable(),
  mission: z.string().nullable(),
  strategicTier: z.string().nullable(),
  ownerId: Uuid.nullable(),
  lifecycle: Lifecycle,
  health: ProductHealth,
  /** 0–100. Null when no health model has run — never coerced to zero. */
  healthScore: z.number().min(0).max(100).nullable(),
});
export type Product = z.infer<typeof Product>;

export const ProductCandidate = withEnvelope('ProductCandidate', {
  sponsorId: Uuid.nullable(),
  currentScope: z.string().nullable(),
  assessmentStatus: DecisionStatus,
});
export type ProductCandidate = z.infer<typeof ProductCandidate>;

export const ProductClassification = withEnvelope('ProductClassification', {
  /** The candidate or product being classified. */
  subjectId: Uuid,
  subjectType: z.string().min(1),
  classCode: ClassificationClass,
  /** Scoring model that produced the result. Two runs of a version must agree. */
  modelVersion: z.string().min(1),
  score: z.number().nullable(),
  effectiveDate: IsoDate,
});
export type ProductClassification = z.infer<typeof ProductClassification>;

export const ProductBoundary = withEnvelope('ProductBoundary', {
  productId: Uuid,
  /** Monotonic. Only one published version is current; earlier ones are kept. */
  boundaryVersion: z.number().int().positive(),
  summary: z.string().nullable(),
  status: DecisionStatus,
  effectiveDate: IsoDate,
});
export type ProductBoundary = z.infer<typeof ProductBoundary>;

export const ProductMission = withEnvelope('ProductMission', {
  productId: Uuid,
  statement: z.string().min(1),
  customerPromise: z.string().nullable(),
  reviewDate: IsoDate.nullable(),
});
export type ProductMission = z.infer<typeof ProductMission>;

export const CustomerSegment = withEnvelope('CustomerSegment', {
  /** Internal consumers are first-class: most platform products have no external segment. */
  segmentType: z.enum(['Internal', 'External']),
  needs: z.string().nullable(),
  channel: z.string().nullable(),
});
export type CustomerSegment = z.infer<typeof CustomerSegment>;

// ── Outcome (ENT-011…014) ────────────────────────────────────────────────────

export const Outcome = withEnvelope('Outcome', {
  outcomeType: z.enum(['Customer', 'Operational', 'Financial', 'Risk']),
  statement: z.string().min(1),
  ownerId: Uuid.nullable(),
});
export type Outcome = z.infer<typeof Outcome>;

export const OKR = withEnvelope('OKR', {
  objective: z.string().min(1),
  periodStart: IsoDate,
  periodEnd: IsoDate,
  ownerId: Uuid.nullable(),
});
export type OKR = z.infer<typeof OKR>;

/**
 * ENT-013. Exported as `MetricDefinitionEntity` because `MetricDefinition` is
 * already the read-side contract in metric.ts and both are re-exported from the
 * package root. The entityType literal and the ENTITY_CODES key remain
 * 'MetricDefinition' — the rename is a TypeScript collision, not a semantic one.
 */
export const MetricDefinitionEntity = withEnvelope('MetricDefinition', {
  unit: MetricUnit,
  /** The subject and period a value is meaningful at, e.g. 'product · month'. */
  grain: z.string().min(1),
  formulaVersion: z.string().min(1),
  ownerId: Uuid.nullable(),
});
export type MetricDefinitionEntity = z.infer<typeof MetricDefinitionEntity>;

export const MetricObservation = withEnvelope('MetricObservation', {
  metricDefinitionId: Uuid,
  subjectId: Uuid,
  subjectType: z.string().min(1),
  periodStart: IsoDate,
  periodEnd: IsoDate,
  /** Nullable: "not measured" is a real observation and must not read as zero. */
  value: z.union([z.number(), z.string(), Money]).nullable(),
});
export type MetricObservation = z.infer<typeof MetricObservation>;

// ── Process (ENT-015…016) ────────────────────────────────────────────────────

export const Process = withEnvelope('Process', {
  level: ProcessLevel,
  parentProcessId: Uuid.nullable(),
  ownerId: Uuid.nullable(),
  criticality: Criticality,
});
export type Process = z.infer<typeof Process>;

export const ProcessStep = withEnvelope('ProcessStep', {
  processId: Uuid,
  sequence: z.number().int().positive(),
  actor: z.string().nullable(),
  input: z.string().nullable(),
  output: z.string().nullable(),
});
export type ProcessStep = z.infer<typeof ProcessStep>;

// ── Technology (ENT-017…022) ─────────────────────────────────────────────────

export const Application = withEnvelope('Application', {
  ownerId: Uuid.nullable(),
  criticality: Criticality,
  lifecycle: Lifecycle,
  technicalHealth: TechnicalHealth,
  /**
   * The recommended portfolio action. Nullable because a recommendation is a
   * governed decision, not a default: an unassessed application is not "Retain".
   */
  disposition: Disposition.nullable(),
});
export type Application = z.infer<typeof Application>;

export const Service = withEnvelope('Service', {
  serviceType: z.string().nullable(),
  ownerId: Uuid.nullable(),
  /** Service level objective as published, e.g. '99.9% monthly availability'. */
  slo: z.string().nullable(),
  lifecycle: Lifecycle,
});
export type Service = z.infer<typeof Service>;

export const API = withEnvelope('API', {
  protocol: z.string().nullable(),
  /** Interface version, distinct from the envelope's rowVersion. */
  apiVersion: z.string().nullable(),
  ownerId: Uuid.nullable(),
  lifecycle: Lifecycle,
});
export type API = z.infer<typeof API>;

export const DataAsset = withEnvelope('DataAsset', {
  assetType: z.string().nullable(),
  ownerId: Uuid.nullable(),
  schemaRef: z.string().nullable(),
});
export type DataAsset = z.infer<typeof DataAsset>;

export const DataProduct = withEnvelope('DataProduct', {
  consumerSummary: z.string().nullable(),
  contractRef: z.string().nullable(),
  qualitySlo: z.string().nullable(),
  ownerId: Uuid.nullable(),
});
export type DataProduct = z.infer<typeof DataProduct>;

export const TechnologyComponent = withEnvelope('TechnologyComponent', {
  componentType: z.string().nullable(),
  componentVersion: z.string().nullable(),
  ownerId: Uuid.nullable(),
  lifecycle: Lifecycle,
});
export type TechnologyComponent = z.infer<typeof TechnologyComponent>;

// ── People (ENT-023…027) ─────────────────────────────────────────────────────

/**
 * Deliberately minimal. This is a platform identity for ownership and workflow,
 * not an HR record — the entity model classifies it Restricted and the model
 * carries no workforce attributes beyond what accountability requires.
 */
export const Person = withEnvelope('Person', {
  displayName: z.string().min(1),
  email: z.string().email().nullable(),
  active: z.boolean(),
});
export type Person = z.infer<typeof Person>;

export const Role = withEnvelope('Role', {
  permissions: z.array(FunctionalPermission),
});
export type Role = z.infer<typeof Role>;

/**
 * Authorization is effective-dated like everything else, so that "who could see
 * this on the reporting date" is answerable after the fact.
 */
export const AccessScope = withEnvelope('AccessScope', {
  personId: Uuid,
  roleId: Uuid,
  /** Grant point in the hierarchy. Descendants inherit unless policy says otherwise. */
  hierarchyNodeId: Uuid,
  hierarchyNodeType: z.string().min(1),
});
export type AccessScope = z.infer<typeof AccessScope>;

export const Team = withEnvelope('Team', {
  teamType: z.string().nullable(),
  ownerId: Uuid.nullable(),
  locationMix: z.string().nullable(),
  lifecycle: Lifecycle,
});
export type Team = z.infer<typeof Team>;

/** Aggregate only. Per-person capacity is out of scope by workforce policy. */
export const TeamCapacity = withEnvelope('TeamCapacity', {
  teamId: Uuid,
  periodStart: IsoDate,
  periodEnd: IsoDate,
  fte: z.number().nonnegative().nullable(),
  contractorFte: z.number().nonnegative().nullable(),
  capacityUnit: z.string().min(1),
});
export type TeamCapacity = z.infer<typeof TeamCapacity>;

// ── Delivery (ENT-028…030) ───────────────────────────────────────────────────

export const WorkItem = withEnvelope('WorkItem', {
  workType: z.enum(['Epic', 'Feature', 'Story', 'Defect', 'Task']),
  teamId: Uuid.nullable(),
  productId: Uuid.nullable(),
  plannedStartDate: IsoDate.nullable(),
  plannedEndDate: IsoDate.nullable(),
  startedAt: Timestamp.nullable(),
  completedAt: Timestamp.nullable(),
});
export type WorkItem = z.infer<typeof WorkItem>;

export const Release = withEnvelope('Release', {
  productId: Uuid.nullable(),
  plannedDate: IsoDate.nullable(),
  actualDate: IsoDate.nullable(),
});
export type Release = z.infer<typeof Release>;

export const Deployment = withEnvelope('Deployment', {
  releaseId: Uuid,
  environment: z.string().min(1),
  deployedAt: Timestamp,
});
export type Deployment = z.infer<typeof Deployment>;

// ── Reliability (ENT-031…033) ────────────────────────────────────────────────

export const Incident = withEnvelope('Incident', {
  severity: z.enum(['Sev1', 'Sev2', 'Sev3', 'Sev4']),
  openedAt: Timestamp,
  closedAt: Timestamp.nullable(),
  productId: Uuid.nullable(),
  serviceId: Uuid.nullable(),
});
export type Incident = z.infer<typeof Incident>;

export const Change = withEnvelope('Change', {
  changeType: z.enum(['Standard', 'Normal', 'Emergency']),
  risk: z.enum(['Low', 'Medium', 'High']),
  implementationDate: IsoDate.nullable(),
});
export type Change = z.infer<typeof Change>;

export const Risk = withEnvelope('Risk', {
  category: z.enum(['Business', 'Technology', 'Delivery', 'Data', 'Commercial']),
  likelihood: z.enum(['Rare', 'Unlikely', 'Possible', 'Likely', 'AlmostCertain']),
  impact: z.enum(['Negligible', 'Minor', 'Moderate', 'Major', 'Severe']),
  ownerId: Uuid.nullable(),
});
export type Risk = z.infer<typeof Risk>;

// ── Finance (ENT-034…037) ────────────────────────────────────────────────────

export const FundingPlan = withEnvelope('FundingPlan', {
  subjectId: Uuid,
  subjectType: z.string().min(1),
  periodStart: IsoDate,
  periodEnd: IsoDate,
  amount: Money,
  fundingType: z.enum(['Capex', 'Opex', 'Mixed', 'Unknown']),
});
export type FundingPlan = z.infer<typeof FundingPlan>;

export const CostActual = withEnvelope('CostActual', {
  subjectId: Uuid,
  subjectType: z.string().min(1),
  periodStart: IsoDate,
  periodEnd: IsoDate,
  amount: Money,
  costType: z.string().nullable(),
});
export type CostActual = z.infer<typeof CostActual>;

/**
 * The result of applying an allocation rule, kept as a record rather than
 * computed on read: the remainder must be reportable as unallocated, and that is
 * only possible if what *was* allocated is stored explicitly.
 */
export const CostAllocation = withEnvelope('CostAllocation', {
  sourceCostId: Uuid,
  targetId: Uuid,
  targetType: z.string().min(1),
  /** 0–1, not 0–100, so it composes with other ratios without unit confusion. */
  percentage: z.number().min(0).max(1),
  amount: Money.nullable(),
  basis: AllocationBasis,
  allocationStatus: AllocationStatus,
  ruleVersion: z.string().min(1),
});
export type CostAllocation = z.infer<typeof CostAllocation>;

export const Benefit = withEnvelope('Benefit', {
  benefitType: z.string().nullable(),
  baseline: Money.nullable(),
  target: Money.nullable(),
  /** Null until value is evidenced. A forecast is never reported as realized. */
  realized: Money.nullable(),
  ownerId: Uuid.nullable(),
});
export type Benefit = z.infer<typeof Benefit>;

// ── Sourcing (ENT-038…041) ───────────────────────────────────────────────────

export const Supplier = withEnvelope('Supplier', {
  legalName: z.string().min(1),
  category: z.string().nullable(),
  riskStatus: z.enum(['Low', 'Medium', 'High', 'Unknown']),
});
export type Supplier = z.infer<typeof Supplier>;

export const Contract = withEnvelope('Contract', {
  supplierId: Uuid,
  title: z.string().min(1),
  startDate: IsoDate,
  endDate: IsoDate.nullable(),
});
export type Contract = z.infer<typeof Contract>;

export const StatementOfWork = withEnvelope('StatementOfWork', {
  contractId: Uuid,
  scope: z.string().nullable(),
  startDate: IsoDate,
  endDate: IsoDate.nullable(),
  /** Not-to-exceed value. Null where the SOW is drawn down against the contract. */
  ceiling: Money.nullable(),
});
export type StatementOfWork = z.infer<typeof StatementOfWork>;

export const ContractObligation = withEnvelope('ContractObligation', {
  contractId: Uuid,
  dueDate: IsoDate.nullable(),
  ownerId: Uuid.nullable(),
});
export type ContractObligation = z.infer<typeof ContractObligation>;

// ── Scenario (ENT-042…044) ───────────────────────────────────────────────────

export const Scenario = withEnvelope('Scenario', {
  scenarioType: ScenarioType,
  /** The baseline a comparison is measured against. Absent it, deltas are meaningless. */
  baselineSnapshotId: Uuid.nullable(),
  ownerId: Uuid.nullable(),
  status: DecisionStatus,
});
export type Scenario = z.infer<typeof Scenario>;

export const ScenarioAssumption = withEnvelope('ScenarioAssumption', {
  scenarioId: Uuid,
  assumptionCode: z.string().min(1),
  value: z.number(),
  unit: z.string().min(1),
  /** Where the number came from. An assumption without a source is an opinion. */
  source: z.string().nullable(),
  confidenceBand: ConfidenceBand.nullable(),
});
export type ScenarioAssumption = z.infer<typeof ScenarioAssumption>;

export const ScenarioResult = withEnvelope('ScenarioResult', {
  scenarioId: Uuid,
  metricDefinitionId: Uuid,
  value: z.union([z.number(), z.string(), Money]).nullable(),
  formulaVersion: z.string().min(1),
});
export type ScenarioResult = z.infer<typeof ScenarioResult>;

// ── Governance (ENT-045…049) ─────────────────────────────────────────────────

export const Decision = withEnvelope('Decision', {
  decisionType: z.string().min(1),
  subjectId: Uuid,
  subjectType: z.string().min(1),
  outcome: z.string().min(1),
  /** Required by governance: a decision without a rationale cannot be reviewed. */
  rationale: z.string().min(1),
  effectiveDate: IsoDate,
  status: DecisionStatus,
});
export type Decision = z.infer<typeof Decision>;

export const Approval = withEnvelope('Approval', {
  subjectId: Uuid,
  subjectType: z.string().min(1),
  stage: z.string().min(1),
  approverId: Uuid,
  action: z.enum(['Submitted', 'Reviewed', 'Returned', 'Approved', 'Rejected', 'Published']),
  decidedAt: Timestamp,
  comments: z.string().nullable(),
});
export type Approval = z.infer<typeof Approval>;

export const Evidence = withEnvelope('Evidence', {
  evidenceType: z.enum(['File', 'Url', 'SourceRecord', 'Assertion']),
  /** File id, URL or source-record reference, interpreted per `evidenceType`. */
  reference: z.string().min(1),
  ownerId: Uuid.nullable(),
  evidenceDate: IsoDate.nullable(),
  /** Restricted evidence still shows that it exists, which is why this is a code. */
  availability: EvidenceStatus,
});
export type Evidence = z.infer<typeof Evidence>;

export const ActionItem = withEnvelope('ActionItem', {
  subjectId: Uuid,
  subjectType: z.string().min(1),
  ownerId: Uuid.nullable(),
  dueDate: IsoDate.nullable(),
});
export type ActionItem = z.infer<typeof ActionItem>;

export const Comment = withEnvelope('Comment', {
  subjectId: Uuid,
  subjectType: z.string().min(1),
  authorId: Uuid,
  text: z.string().min(1),
  postedAt: Timestamp,
});
export type Comment = z.infer<typeof Comment>;

// ── Data Management (ENT-050…057) ────────────────────────────────────────────

export const SourceSystem = withEnvelope('SourceSystem', {
  sourceType: z.string().min(1),
  ownerId: Uuid.nullable(),
  mode: SourceMode,
});
export type SourceSystem = z.infer<typeof SourceSystem>;

export const FileArtifact = withEnvelope('FileArtifact', {
  fileName: z.string().min(1),
  /** Content hash. Re-uploading the same file must not create a second lineage. */
  hash: z.string().min(1),
  mediaType: z.string().min(1),
  storageUri: z.string().min(1),
  templateVersion: z.string().nullable(),
});
export type FileArtifact = z.infer<typeof FileArtifact>;

export const IngestionRunCounts = z.object({
  submitted: z.number().int().nonnegative(),
  read: z.number().int().nonnegative(),
  valid: z.number().int().nonnegative(),
  warning: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  unchanged: z.number().int().nonnegative(),
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
});
export type IngestionRunCounts = z.infer<typeof IngestionRunCounts>;

export const IngestionRun = withEnvelope('IngestionRun', {
  sourceId: Uuid,
  fileId: Uuid.nullable(),
  status: IngestionRunStatus,
  counts: IngestionRunCounts,
  startedAt: Timestamp,
  completedAt: Timestamp.nullable(),
});
export type IngestionRun = z.infer<typeof IngestionRun>;

/**
 * Immutable. The raw row is kept so that any canonical value can be traced back
 * to what the file actually said — without letting the payload be queried as if
 * it were canonical data.
 */
export const SourceRecord = withEnvelope('SourceRecord', {
  runId: Uuid,
  sheet: z.string().nullable(),
  rowNumber: z.number().int().positive().nullable(),
  payloadHash: z.string().min(1),
  payload: z.record(z.unknown()),
});
export type SourceRecord = z.infer<typeof SourceRecord>;

export const MappingRule = withEnvelope('MappingRule', {
  sourceField: z.string().min(1),
  targetField: z.string().min(1),
  ruleType: TransformationType,
  expression: z.string().min(1),
  ruleVersion: z.string().min(1),
  status: MappingRuleLifecycle,
});
export type MappingRule = z.infer<typeof MappingRule>;

export const DataQualityRule = withEnvelope('DataQualityRule', {
  /** One of the six DQ dimensions: completeness, validity, uniqueness, … */
  dimension: z.string().min(1),
  expression: z.string().min(1),
  severity: IssueSeverity,
  ownerId: Uuid.nullable(),
  ruleVersion: z.string().min(1),
});
export type DataQualityRule = z.infer<typeof DataQualityRule>;

export const DataQualityIssue = withEnvelope('DataQualityIssue', {
  ruleId: Uuid,
  subjectId: Uuid,
  subjectType: z.string().min(1),
  severity: IssueSeverity,
  assigneeId: Uuid.nullable(),
});
export type DataQualityIssue = z.infer<typeof DataQualityIssue>;

export const LineageEdge = withEnvelope('LineageEdge', {
  /** Opaque field/entity references, e.g. 'SourceRecord:<id>#column' → 'Product.name'. */
  fromRef: z.string().min(1),
  toRef: z.string().min(1),
  mappingRuleId: Uuid.nullable(),
  runId: Uuid.nullable(),
});
export type LineageEdge = z.infer<typeof LineageEdge>;

// ── Platform (ENT-058…061) ───────────────────────────────────────────────────

/**
 * Append-only. Values are stored as hashes rather than content so that the trail
 * can be complete without becoming a second copy of restricted data.
 */
export const AuditEvent = withEnvelope('AuditEvent', {
  actorId: Uuid.nullable(),
  action: z.string().min(1),
  subjectId: Uuid.nullable(),
  subjectType: z.string().min(1),
  oldValueHash: z.string().nullable(),
  newValueHash: z.string().nullable(),
  /** Ties every event of one request together across API, worker and outbox. */
  correlationId: z.string().min(1),
  occurredAt: Timestamp,
});
export type AuditEvent = z.infer<typeof AuditEvent>;

export const BackgroundJob = withEnvelope('BackgroundJob', {
  jobType: z.string().min(1),
  /** 0–1. Null while the job has not reported progress. */
  progress: z.number().min(0).max(1).nullable(),
  requestedBy: Uuid,
  resultRef: z.string().nullable(),
});
export type BackgroundJob = z.infer<typeof BackgroundJob>;

export const Notification = withEnvelope('Notification', {
  recipientId: Uuid,
  notificationType: z.string().min(1),
  subjectId: Uuid.nullable(),
  subjectType: z.string().nullable(),
});
export type Notification = z.infer<typeof Notification>;

export const ConfigurationItem = withEnvelope('ConfigurationItem', {
  /** Tenant, domain or hierarchy node the setting applies to. */
  scope: z.string().min(1),
  value: z.unknown(),
  configVersion: z.number().int().positive(),
});
export type ConfigurationItem = z.infer<typeof ConfigurationItem>;

// ── Entity type registry ─────────────────────────────────────────────────────

/**
 * The canonical entity type codes. Relationship endpoints, evidence subjects,
 * audit subjects and mapping targets are all typed as strings on the wire; this
 * enum is what turns those strings back into something checkable.
 */
export const EntityType = z.enum([
  // Organization
  'Enterprise',
  'OrganizationalUnit',
  'ValueStream',
  'ProductGroup',
  // Product
  'Product',
  'ProductCandidate',
  'ProductClassification',
  'ProductBoundary',
  'ProductMission',
  'CustomerSegment',
  // Outcome
  'Outcome',
  'OKR',
  'MetricDefinition',
  'MetricObservation',
  // Process
  'Process',
  'ProcessStep',
  // Technology
  'Application',
  'Service',
  'API',
  'DataAsset',
  'DataProduct',
  'TechnologyComponent',
  // People
  'Person',
  'Role',
  'AccessScope',
  'Team',
  'TeamCapacity',
  // Delivery
  'WorkItem',
  'Release',
  'Deployment',
  // Reliability
  'Incident',
  'Change',
  'Risk',
  // Finance
  'FundingPlan',
  'CostActual',
  'CostAllocation',
  'Benefit',
  // Sourcing
  'Supplier',
  'Contract',
  'StatementOfWork',
  'ContractObligation',
  // Scenario
  'Scenario',
  'ScenarioAssumption',
  'ScenarioResult',
  // Governance
  'Decision',
  'Approval',
  'Evidence',
  'ActionItem',
  'Comment',
  // Data Management
  'SourceSystem',
  'FileArtifact',
  'IngestionRun',
  'SourceRecord',
  'MappingRule',
  'DataQualityRule',
  'DataQualityIssue',
  'LineageEdge',
  // Platform
  'AuditEvent',
  'BackgroundJob',
  'Notification',
  'ConfigurationItem',
]);
export type EntityType = z.infer<typeof EntityType>;

/**
 * ENT- codes from the entity catalog. Kept as data rather than baked into each
 * schema because the codes are how the business, the spreadsheet templates and
 * the specification pack refer to entities — engineers and stewards need to be
 * able to translate between the two vocabularies in both directions.
 */
export const ENTITY_CODES: Record<string, string> = {
  Enterprise: 'ENT-001',
  OrganizationalUnit: 'ENT-002',
  ValueStream: 'ENT-003',
  ProductGroup: 'ENT-004',
  Product: 'ENT-005',
  ProductCandidate: 'ENT-006',
  ProductClassification: 'ENT-007',
  ProductBoundary: 'ENT-008',
  ProductMission: 'ENT-009',
  CustomerSegment: 'ENT-010',
  Outcome: 'ENT-011',
  OKR: 'ENT-012',
  MetricDefinition: 'ENT-013',
  MetricObservation: 'ENT-014',
  Process: 'ENT-015',
  ProcessStep: 'ENT-016',
  Application: 'ENT-017',
  Service: 'ENT-018',
  API: 'ENT-019',
  DataAsset: 'ENT-020',
  DataProduct: 'ENT-021',
  TechnologyComponent: 'ENT-022',
  Person: 'ENT-023',
  Role: 'ENT-024',
  AccessScope: 'ENT-025',
  Team: 'ENT-026',
  TeamCapacity: 'ENT-027',
  WorkItem: 'ENT-028',
  Release: 'ENT-029',
  Deployment: 'ENT-030',
  Incident: 'ENT-031',
  Change: 'ENT-032',
  Risk: 'ENT-033',
  FundingPlan: 'ENT-034',
  CostActual: 'ENT-035',
  CostAllocation: 'ENT-036',
  Benefit: 'ENT-037',
  Supplier: 'ENT-038',
  Contract: 'ENT-039',
  StatementOfWork: 'ENT-040',
  ContractObligation: 'ENT-041',
  Scenario: 'ENT-042',
  ScenarioAssumption: 'ENT-043',
  ScenarioResult: 'ENT-044',
  Decision: 'ENT-045',
  Approval: 'ENT-046',
  Evidence: 'ENT-047',
  ActionItem: 'ENT-048',
  Comment: 'ENT-049',
  SourceSystem: 'ENT-050',
  FileArtifact: 'ENT-051',
  IngestionRun: 'ENT-052',
  SourceRecord: 'ENT-053',
  MappingRule: 'ENT-054',
  DataQualityRule: 'ENT-055',
  DataQualityIssue: 'ENT-056',
  LineageEdge: 'ENT-057',
  AuditEvent: 'ENT-058',
  BackgroundJob: 'ENT-059',
  Notification: 'ENT-060',
  ConfigurationItem: 'ENT-061',
};

/**
 * Default sensitivity per the catalog's Sensitivity column.
 *
 * This is the floor, not the ceiling: a record may be classified more strictly
 * by field policy or by tenant configuration, and the envelope's own
 * `dataClassification` is what an API response is filtered against. The map
 * exists so that seed data, templates and new records start at the classification
 * the specification requires instead of at whatever the importer felt like.
 */
export const ENTITY_SENSITIVITY: Record<string, DataClassification> = {
  Enterprise: 'Internal',
  OrganizationalUnit: 'Internal',
  ValueStream: 'Internal',
  ProductGroup: 'Internal',
  Product: 'Confidential',
  ProductCandidate: 'Confidential',
  ProductClassification: 'Confidential',
  ProductBoundary: 'Confidential',
  ProductMission: 'Internal',
  CustomerSegment: 'Confidential',
  Outcome: 'Confidential',
  OKR: 'Confidential',
  MetricDefinition: 'Internal',
  MetricObservation: 'Restricted',
  Process: 'Confidential',
  ProcessStep: 'Confidential',
  Application: 'Confidential',
  Service: 'Confidential',
  API: 'Confidential',
  DataAsset: 'Restricted',
  DataProduct: 'Restricted',
  TechnologyComponent: 'Confidential',
  Person: 'Restricted',
  Role: 'Internal',
  AccessScope: 'Restricted',
  Team: 'Restricted',
  TeamCapacity: 'Restricted',
  WorkItem: 'Confidential',
  Release: 'Confidential',
  Deployment: 'Confidential',
  Incident: 'Restricted',
  Change: 'Restricted',
  Risk: 'Restricted',
  FundingPlan: 'Restricted',
  CostActual: 'Restricted',
  CostAllocation: 'Restricted',
  Benefit: 'Restricted',
  Supplier: 'Restricted',
  Contract: 'Restricted',
  StatementOfWork: 'Restricted',
  ContractObligation: 'Restricted',
  Scenario: 'Restricted',
  ScenarioAssumption: 'Restricted',
  ScenarioResult: 'Restricted',
  Decision: 'Restricted',
  Approval: 'Restricted',
  Evidence: 'Confidential',
  ActionItem: 'Confidential',
  Comment: 'Confidential',
  SourceSystem: 'Confidential',
  FileArtifact: 'Restricted',
  IngestionRun: 'Confidential',
  SourceRecord: 'Restricted',
  MappingRule: 'Confidential',
  DataQualityRule: 'Confidential',
  DataQualityIssue: 'Restricted',
  LineageEdge: 'Restricted',
  AuditEvent: 'Restricted',
  BackgroundJob: 'Confidential',
  Notification: 'Confidential',
  ConfigurationItem: 'Restricted',
};

/** Every entity type name, in ENT- code order. Useful for endpoint wildcards. */
export const ALL_ENTITY_TYPES: readonly EntityType[] = EntityType.options;
